/* eslint-disable react/jsx-no-target-blank */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { AsideMenuItemWithSub } from './AsideMenuItemWithSub'
import { AsideMenuItem } from './AsideMenuItem'
import { usePageData } from '../../core'
import type { SiteOption } from '../../core'
import { PAGE_PERMISSION } from '../../../../app/modules/roles/constant/role-default'
import { getLocations } from '../../../../app/modules/locations/api'
import { Portal } from '../../../partials/content/portal/Portal'

type SiteResponseItem = {
  id?: number | string | null
  name?: string | null
  ouName?: string | null
  organizationUnitName?: string | null
  organizationUnitLongshipId?: string | null
  organizationUnitParentId?: string | null
  organizationUnitHierarchyIds?: Array<string | null> | null
  organizationUnitHierarchyNames?: Array<string | null> | null
}

const SITE_PAGE_SIZE = 100

type SiteTreeNode = {
  id: string
  name: string
  childOrganizationUnits: SiteTreeNode[]
  locations: SiteOption[]
}

type SiteTreeData = {
  topLevelSites: SiteOption[]
  rootNodes: SiteTreeNode[]
  ungroupedSites: SiteOption[]
  sitePathById: Map<number, string[]>
  siteIdsByNodeId: Map<string, number[]>
  collapsedSiteIds: Set<number>
}

type SiteTreeItemProps = {
  node: SiteTreeNode
  expandedNodeIds: Record<string, boolean>
  toggleNode: (nodeId: string) => void
  selectedSiteId: number | null
  selectedSiteIds: number[] | null
  setSelectedSiteId: (siteId: number | null) => void
  setSelectedSiteIds: (siteIds: number[] | null) => void
  siteIdsByNodeId: Map<string, number[]>
  nodeSortLabelsById: Map<string, string>
  collapsedSiteIds: Set<number>
  isSiteLoading: boolean
  forceExpanded?: boolean
  onSiteSelected?: () => void
  visitedNodeIds?: Set<string>
}

const toSanitizedStringArray = (value: Array<string | null> | null | undefined) =>
  (Array.isArray(value) ? value : [])
    .map((item) => item?.trim() ?? '')
    .filter((item) => item.length > 0)

const toSiteOptions = (items: SiteResponseItem[]): SiteOption[] => {
  const uniqueSites = new Map<number, SiteOption>()

  items.forEach((item) => {
    if (item.id === null || item.id === undefined) {
      return
    }

    const numericId = Number(item.id)
    if (!Number.isFinite(numericId)) {
      return
    }

    const existing = uniqueSites.get(numericId)
    const trimmedName = item.name?.trim()
    const organizationUnitName = item.organizationUnitName?.trim() || item.ouName?.trim() || ''
    const organizationUnitLongshipId = item.organizationUnitLongshipId?.trim() || ''
    const organizationUnitParentId = item.organizationUnitParentId?.trim() || ''
    const organizationUnitHierarchyIds = toSanitizedStringArray(item.organizationUnitHierarchyIds)
    const organizationUnitHierarchyNames = toSanitizedStringArray(item.organizationUnitHierarchyNames)

    uniqueSites.set(numericId, {
      id: numericId,
      name: trimmedName || existing?.name || `Site ${numericId}`,
      organizationUnitName: organizationUnitName || existing?.organizationUnitName || undefined,
      organizationUnitLongshipId: organizationUnitLongshipId || existing?.organizationUnitLongshipId || undefined,
      organizationUnitParentId: organizationUnitParentId || existing?.organizationUnitParentId || undefined,
      organizationUnitHierarchyIds:
        organizationUnitHierarchyIds.length > 0
          ? organizationUnitHierarchyIds
          : existing?.organizationUnitHierarchyIds,
      organizationUnitHierarchyNames:
        organizationUnitHierarchyNames.length > 0
          ? organizationUnitHierarchyNames
          : existing?.organizationUnitHierarchyNames,
    })
  })

  return Array.from(uniqueSites.values()).sort((left, right) => left.name.localeCompare(right.name))
}

const matchesSearchTerm = (value: string | null | undefined, searchTerm: string) =>
  (value ?? '').trim().toLowerCase().includes(searchTerm)

const SELECTED_SITE_OPTION_COLOR = '#44d62c'

const normalizeSiteIds = (siteIds: number[] | null | undefined) => {
  if (!Array.isArray(siteIds)) {
    return []
  }

  return Array.from(
    new Set(
      siteIds
        .map((siteId) => Number(siteId))
        .filter((siteId) => Number.isFinite(siteId))
    )
  ).sort((left, right) => left - right)
}

const areSameSiteIdSets = (left: number[] | null | undefined, right: number[] | null | undefined) => {
  const normalizedLeft = normalizeSiteIds(left)
  const normalizedRight = normalizeSiteIds(right)

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  return normalizedLeft.every((siteId, index) => siteId === normalizedRight[index])
}

const isSiteSelected = (
  selectedSiteId: number | null,
  selectedSiteIds: number[] | null | undefined,
  siteId: number
) => {
  if (selectedSiteId === siteId) {
    return true
  }

  if (selectedSiteId !== null) {
    return false
  }

  return areSameSiteIdSets(selectedSiteIds, [siteId])
}

const getSiteOptionClassName = (isSelected: boolean) =>
  `w-100 border-0 bg-transparent text-start py-1 px-2 d-flex align-items-start ${
    isSelected ? 'fw-semibold' : 'text-gray-700'
  }`

const getSiteIconClassName = (isSelected: boolean) =>
  `bi bi-building me-2 mt-1 flex-shrink-0 ${isSelected ? '' : 'text-gray-500'}`

const getSiteOptionStyle = (isSelected: boolean): React.CSSProperties => ({
  color: isSelected ? SELECTED_SITE_OPTION_COLOR : undefined,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.35,
})

const compareSiteSelectorLabels = (left: string, right: string) =>
  left.localeCompare(right, undefined, {numeric: true, sensitivity: 'base'})

const getSiteHierarchyNamesForSort = (site: SiteOption) => {
  const hierarchyNames = toSanitizedStringArray(site.organizationUnitHierarchyNames ?? null).filter(
    (name) => name.trim().toLowerCase() !== 'charge hub'
  )

  if (hierarchyNames.length > 0) {
    return hierarchyNames
  }

  const fallbackGroupName = site.organizationUnitName?.trim() ?? ''
  if (fallbackGroupName && fallbackGroupName.toLowerCase() !== 'charge hub') {
    return [fallbackGroupName]
  }

  return []
}

const getSiteSortLabel = (site: SiteOption, collapsedSiteIds: Set<number> = new Set<number>()) =>
  collapsedSiteIds.has(site.id) ? site.name : [...getSiteHierarchyNamesForSort(site), site.name].join(' / ')

const buildNodeSortLabels = (
  nodes: SiteTreeNode[],
  parentPath: string[] = [],
  visitedNodeIds: Set<string> = new Set<string>(),
  labelsById: Map<string, string> = new Map<string, string>()
) => {
  nodes.forEach((node) => {
    if (visitedNodeIds.has(node.id)) {
      return
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)
    const nextPath = [...parentPath, node.name]
    labelsById.set(node.id, nextPath.join(' / '))
    buildNodeSortLabels(node.childOrganizationUnits, nextPath, nextVisitedNodeIds, labelsById)
  })

  return labelsById
}

const findNodePathById = (
  nodes: SiteTreeNode[],
  targetNodeId: string,
  visitedNodeIds: Set<string> = new Set(),
  path: string[] = []
): string[] | null => {
  for (const node of nodes) {
    if (visitedNodeIds.has(node.id)) {
      continue
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)
    const nextPath = [...path, node.id]

    if (node.id === targetNodeId) {
      return nextPath
    }

    const childPath = findNodePathById(node.childOrganizationUnits, targetNodeId, nextVisitedNodeIds, nextPath)
    if (childPath) {
      return childPath
    }
  }

  return null
}

const appendUniqueSite = (sites: SiteOption[], site: SiteOption) => {
  if (!sites.some((existingSite) => existingSite.id === site.id)) {
    sites.push(site)
  }
}

const appendUniqueSites = (targetSites: SiteOption[], sitesToAppend: SiteOption[]) => {
  sitesToAppend.forEach((site) => appendUniqueSite(targetSites, site))
}

const collectUniqueSitesInNode = (
  node: SiteTreeNode,
  visitedNodeIds: Set<string> = new Set<string>()
): SiteOption[] => {
  if (visitedNodeIds.has(node.id)) {
    return []
  }

  const nextVisitedNodeIds = new Set(visitedNodeIds)
  nextVisitedNodeIds.add(node.id)

  const uniqueSites = new Map<number, SiteOption>()
  node.locations.forEach((site) => {
    uniqueSites.set(site.id, site)
  })

  node.childOrganizationUnits.forEach((childNode) => {
    collectUniqueSitesInNode(childNode, nextVisitedNodeIds).forEach((site) => {
      uniqueSites.set(site.id, site)
    })
  })

  return Array.from(uniqueSites.values())
}

const collapseSingleSiteGroups = (
  nodes: SiteTreeNode[],
  visitedNodeIds: Set<string> = new Set<string>()
): {nodes: SiteTreeNode[]; liftedSites: SiteOption[]; liftedSiteIds: Set<number>} => {
  const nextNodes: SiteTreeNode[] = []
  const liftedSites: SiteOption[] = []
  const liftedSiteIds = new Set<number>()

  nodes.forEach((node) => {
    if (visitedNodeIds.has(node.id)) {
      return
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)

    const collapsedChildren = collapseSingleSiteGroups(node.childOrganizationUnits, nextVisitedNodeIds)
    const nextNodeLocations = [...node.locations]
    appendUniqueSites(nextNodeLocations, collapsedChildren.liftedSites)

    const nextNode: SiteTreeNode = {
      ...node,
      childOrganizationUnits: collapsedChildren.nodes,
      locations: nextNodeLocations,
    }

    const descendantSites = collectUniqueSitesInNode(nextNode)
    if (descendantSites.length === 1) {
      appendUniqueSite(liftedSites, descendantSites[0])
      liftedSiteIds.add(descendantSites[0].id)
      return
    }

    nextNodes.push(nextNode)
    collapsedChildren.liftedSiteIds.forEach((siteId) => {
      liftedSiteIds.add(siteId)
    })
  })

  return {nodes: nextNodes, liftedSites, liftedSiteIds}
}

const buildSiteTree = (sites: SiteOption[]): SiteTreeData => {
  const organizationUnitsById = new Map<string, SiteTreeNode>()
  const parentByChildId = new Map<string, string>()
  const topLevelSites: SiteOption[] = []
  const ungroupedSites: SiteOption[] = []
  const sitePathById = new Map<number, string[]>()

  const ensureNode = (id: string, name: string) => {
    const existing = organizationUnitsById.get(id)
    if (existing) {
      if ((!existing.name || existing.name === existing.id) && name && name !== existing.id) {
        existing.name = name
      }

      return existing
    }

    const node: SiteTreeNode = {
      id,
      name: name || id,
      childOrganizationUnits: [],
      locations: [],
    }

    organizationUnitsById.set(id, node)
    return node
  }

  sites.forEach((site) => {
    const hierarchyIds = toSanitizedStringArray(site.organizationUnitHierarchyIds ?? null)
    const hierarchyNames = toSanitizedStringArray(site.organizationUnitHierarchyNames ?? null)
    const fallbackOuId = site.organizationUnitLongshipId?.trim() || ''
    const fallbackOuName = site.organizationUnitName?.trim() || ''
    const fallbackParentId = site.organizationUnitParentId?.trim() || ''

    let normalizedIds = hierarchyIds.length > 0 ? hierarchyIds : fallbackOuId ? [fallbackOuId] : []
    const normalizedNames =
      hierarchyNames.length > 0 ? hierarchyNames : fallbackOuName ? [fallbackOuName] : []

    if (normalizedIds.length === 0 && normalizedNames.length > 0) {
      normalizedIds = normalizedNames.map(
        (name, index) =>
          `ou-name:${name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')}:${index}`
      )
    }

    if (normalizedIds.length === 0 && normalizedNames.length === 0) {
      ungroupedSites.push(site)
      return
    }

    const pathIds: string[] = []
    normalizedIds.forEach((rawId, index) => {
      const nodeId = rawId.trim()
      if (!nodeId) {
        return
      }

      const nodeName = normalizedNames[index]?.trim() || (index === normalizedIds.length - 1 ? fallbackOuName : '') || nodeId
      ensureNode(nodeId, nodeName)
      pathIds.push(nodeId)

      if (index > 0) {
        const parentId = normalizedIds[index - 1]?.trim()
        if (parentId && parentId !== nodeId) {
          parentByChildId.set(nodeId, parentId)
        }
      }
    })

    if (pathIds.length === 0) {
      ungroupedSites.push(site)
      return
    }

    if (pathIds.length === 1 && fallbackParentId && fallbackParentId !== pathIds[0]) {
      ensureNode(fallbackParentId, fallbackParentId)
      parentByChildId.set(pathIds[0], fallbackParentId)
    }

    const leafNodeId = pathIds[pathIds.length - 1]
    const leafNode = organizationUnitsById.get(leafNodeId)
    if (leafNode && !leafNode.locations.some((existingLocation) => existingLocation.id === site.id)) {
      leafNode.locations.push(site)
    }

    sitePathById.set(site.id, pathIds)
  })

  const rootNodes: SiteTreeNode[] = []
  organizationUnitsById.forEach((node) => {
    node.childOrganizationUnits = []
  })

  organizationUnitsById.forEach((node) => {
    const parentId = parentByChildId.get(node.id)
    if (parentId) {
      const parentNode = organizationUnitsById.get(parentId)
      if (parentNode && parentNode.id !== node.id) {
        parentNode.childOrganizationUnits.push(node)
        return
      }
    }

    rootNodes.push(node)
  })

  const sortNodes = (
    nodes: SiteTreeNode[],
    collapsedSiteIds: Set<number>,
    visited: Set<string> = new Set()
  ) => {
    nodes.sort((left, right) => left.name.localeCompare(right.name))
    nodes.forEach((node) => {
      if (visited.has(node.id)) {
        return
      }

      visited.add(node.id)
      node.locations.sort((left, right) =>
        compareSiteSelectorLabels(
          getSiteSortLabel(left, collapsedSiteIds),
          getSiteSortLabel(right, collapsedSiteIds)
        )
      )
      sortNodes(node.childOrganizationUnits, collapsedSiteIds, visited)
    })
  }

  const normalizedRootNodes = rootNodes.flatMap((node) => {
    const isChargeHubParentGroup =
      node.name.trim().toLowerCase() === 'charge hub'

    if (isChargeHubParentGroup) {
      node.locations.forEach((site) => {
        appendUniqueSite(topLevelSites, site)
      })
    }

    return isChargeHubParentGroup ? node.childOrganizationUnits : [node]
  })

  const collapsedRootNodes = collapseSingleSiteGroups(normalizedRootNodes)
  appendUniqueSites(topLevelSites, collapsedRootNodes.liftedSites)

  topLevelSites.sort((left, right) =>
    compareSiteSelectorLabels(
      getSiteSortLabel(left, collapsedRootNodes.liftedSiteIds),
      getSiteSortLabel(right, collapsedRootNodes.liftedSiteIds)
    )
  )
  sortNodes(collapsedRootNodes.nodes, collapsedRootNodes.liftedSiteIds)
  ungroupedSites.sort((left, right) =>
    compareSiteSelectorLabels(
      getSiteSortLabel(left, collapsedRootNodes.liftedSiteIds),
      getSiteSortLabel(right, collapsedRootNodes.liftedSiteIds)
    )
  )

  const siteIdsByNodeId = new Map<string, number[]>()
  const collectSiteIds = (node: SiteTreeNode, visitedNodeIds: Set<string> = new Set()) => {
    if (visitedNodeIds.has(node.id)) {
      return [] as number[]
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)

    const siteIds = new Set<number>()
    node.locations.forEach((site) => siteIds.add(site.id))

    node.childOrganizationUnits.forEach((childNode) => {
      collectSiteIds(childNode, nextVisitedNodeIds).forEach((siteId) => {
        siteIds.add(siteId)
      })
    })

    const normalizedSiteIds = Array.from(siteIds).sort((left, right) => left - right)
    siteIdsByNodeId.set(node.id, normalizedSiteIds)
    return normalizedSiteIds
  }

  collapsedRootNodes.nodes.forEach((node) => {
    collectSiteIds(node)
  })

  return {
    topLevelSites,
    rootNodes: collapsedRootNodes.nodes,
    ungroupedSites,
    sitePathById,
    siteIdsByNodeId,
    collapsedSiteIds: collapsedRootNodes.liftedSiteIds,
  }
}

const filterSiteTreeNodes = (
  nodes: SiteTreeNode[],
  searchTerm: string,
  visitedNodeIds: Set<string> = new Set<string>()
): SiteTreeNode[] => {
  const filteredNodes: SiteTreeNode[] = []

  nodes.forEach((node) => {
    if (visitedNodeIds.has(node.id)) {
      return
    }

    const nextVisitedNodeIds = new Set(visitedNodeIds)
    nextVisitedNodeIds.add(node.id)

    const nodeMatches = matchesSearchTerm(node.name, searchTerm)
    if (nodeMatches) {
      filteredNodes.push(node)
      return
    }

    const filteredLocations = node.locations.filter((site) => matchesSearchTerm(site.name, searchTerm))
    const filteredChildren = filterSiteTreeNodes(node.childOrganizationUnits, searchTerm, nextVisitedNodeIds)

    if (filteredLocations.length === 0 && filteredChildren.length === 0) {
      return
    }

    filteredNodes.push({
      ...node,
      locations: filteredLocations,
      childOrganizationUnits: filteredChildren,
    })
  })

  return filteredNodes
}

const SiteTreeItem: React.FC<SiteTreeItemProps> = ({
  node,
  expandedNodeIds,
  toggleNode,
  selectedSiteId,
  selectedSiteIds,
  setSelectedSiteId,
  setSelectedSiteIds,
  siteIdsByNodeId,
  nodeSortLabelsById,
  collapsedSiteIds,
  isSiteLoading,
  forceExpanded = false,
  onSiteSelected,
  visitedNodeIds = new Set<string>(),
}) => {
  if (visitedNodeIds.has(node.id)) {
    return null
  }

  const nextVisitedNodeIds = new Set(visitedNodeIds)
  nextVisitedNodeIds.add(node.id)
  const hasChildren = node.childOrganizationUnits.length > 0 || node.locations.length > 0
  const isExpanded = forceExpanded || Boolean(expandedNodeIds[node.id])
  const canToggle = hasChildren && !forceExpanded
  const nodeSiteIds = siteIdsByNodeId.get(node.id) ?? []
  const isGroupSelected = selectedSiteId === null && areSameSiteIdSets(selectedSiteIds, nodeSiteIds)
  const childEntries = [
    ...node.locations.map((site) => ({
      type: 'site' as const,
      key: `site-${site.id}`,
      sortLabel: getSiteSortLabel(site, collapsedSiteIds),
      site,
    })),
    ...node.childOrganizationUnits.map((childNode) => ({
      type: 'group' as const,
      key: `group-${childNode.id}`,
      sortLabel: nodeSortLabelsById.get(childNode.id) ?? childNode.name,
      node: childNode,
    })),
  ].sort((left, right) => compareSiteSelectorLabels(left.sortLabel, right.sortLabel))

  return (
    <div className='mb-1'>
      <div className='d-flex align-items-center'>
        {canToggle ? (
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light w-25px h-25px'
            onClick={() => toggleNode(node.id)}
            disabled={isSiteLoading}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${node.name}`}
          >
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} fs-7`}></i>
          </button>
        ) : (
          <span className='d-inline-block w-25px'></span>
        )}
        {nodeSiteIds.length > 0 ? (
          <button
            type='button'
            className={getSiteOptionClassName(isGroupSelected)}
            style={getSiteOptionStyle(isGroupSelected)}
            data-site-option-selected={isGroupSelected ? 'true' : undefined}
            onClick={() => {
              setSelectedSiteIds(nodeSiteIds)
              onSiteSelected?.()
            }}
            disabled={isSiteLoading}
            title={`${node.name} (${nodeSiteIds.length} site${nodeSiteIds.length === 1 ? '' : 's'})`}
          >
            <span>{node.name}</span>
          </button>
        ) : (
          <span
            className='text-gray-700 fw-semibold fs-8'
            style={{whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35}}
            title={node.name}
          >
            {node.name}
          </span>
        )}
      </div>

      {hasChildren && isExpanded ? (
        <div className='ms-7 mt-1'>
          {childEntries.map((entry) =>
            entry.type === 'site' ? (
              (() => {
                const isSelected = isSiteSelected(selectedSiteId, selectedSiteIds, entry.site.id)
                return (
                  <button
                    key={entry.key}
                    type='button'
                    className={`${getSiteOptionClassName(isSelected)} mb-1`}
                    style={getSiteOptionStyle(isSelected)}
                    data-site-option-selected={isSelected ? 'true' : undefined}
                    onClick={() => {
                      setSelectedSiteId(entry.site.id)
                      onSiteSelected?.()
                    }}
                    disabled={isSiteLoading}
                    title={entry.site.name}
                  >
                    <i className={getSiteIconClassName(isSelected)}></i>
                    <span>{entry.site.name}</span>
                  </button>
                )
              })()
            ) : (
              <SiteTreeItem
                key={entry.key}
                node={entry.node}
                expandedNodeIds={expandedNodeIds}
                toggleNode={toggleNode}
                selectedSiteId={selectedSiteId}
                selectedSiteIds={selectedSiteIds}
                setSelectedSiteId={setSelectedSiteId}
                setSelectedSiteIds={setSelectedSiteIds}
                siteIdsByNodeId={siteIdsByNodeId}
                nodeSortLabelsById={nodeSortLabelsById}
                collapsedSiteIds={collapsedSiteIds}
                isSiteLoading={isSiteLoading}
                forceExpanded={forceExpanded}
                onSiteSelected={onSiteSelected}
                visitedNodeIds={nextVisitedNodeIds}
              />
            )
          )}
        </div>
      ) : null}
    </div>
  )
}

export function AsideMenuMain() {
  const intl = useIntl()
  const [isSiteLoading, setIsSiteLoading] = useState(false)
  const [hasResolvedSiteLoad, setHasResolvedSiteLoad] = useState(false)
  const [isSiteSelectorOpen, setIsSiteSelectorOpen] = useState(false)
  const [siteSearchTerm, setSiteSearchTerm] = useState('')
  const {
    currentUser,
    siteOptions,
    setSiteOptions,
    selectedSiteId,
    setSelectedSiteId,
    selectedSiteIds,
    setSelectedSiteIds,
  } = usePageData()
  const userRoleKey = useMemo(() => currentUser.userRoles.join('|'), [currentUser.userRoles])
  const siteSelectorRef = useRef<HTMLDivElement | null>(null)
  const siteSelectorButtonRef = useRef<HTMLButtonElement | null>(null)
  const siteSelectorDropdownRef = useRef<HTMLDivElement | null>(null)
  const hasAutoScrolledOnOpenRef = useRef(false)
  const [siteSelectorDropdownStyle, setSiteSelectorDropdownStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    let isMounted = true

    const loadSites = async () => {
      if (isMounted) {
        setHasResolvedSiteLoad(false)
      }
      setIsSiteLoading(true)
      try {
        let skip = 0
        let totalCount = 0
        const allItems: SiteResponseItem[] = []

        do {
          const response = await getLocations(skip, SITE_PAGE_SIZE, { field: 'name', dir: 'asc' })
          const pageItems = Array.isArray(response?.data?.items) ? (response.data.items as SiteResponseItem[]) : []
          totalCount = Number(response?.data?.totalCount ?? pageItems.length)
          allItems.push(...pageItems)
          skip += SITE_PAGE_SIZE
        } while (skip < totalCount)

        if (!isMounted) {
          return
        }

        const availableSites = toSiteOptions(allItems)
        setSiteOptions(availableSites)
      } catch {
        if (!isMounted) {
          return
        }

        setSiteOptions([])
      } finally {
        if (isMounted) {
          setIsSiteLoading(false)
          setHasResolvedSiteLoad(true)
        }
      }
    }

    loadSites()
    return () => {
      isMounted = false
    }
  }, [currentUser.id, setSiteOptions, userRoleKey])

  useEffect(() => {
    if (!hasResolvedSiteLoad) {
      return
    }

    if (siteOptions.length === 0) {
      if (selectedSiteId !== null || (selectedSiteIds !== null && selectedSiteIds.length > 0)) {
        setSelectedSiteIds(null)
      }
      return
    }

    const availableSiteIds = new Set(siteOptions.map((site) => site.id))
    const normalizedSelectedSiteIds = normalizeSiteIds(selectedSiteIds)

    if (normalizedSelectedSiteIds.length > 0) {
      const availableSelectedSiteIds = normalizedSelectedSiteIds.filter((siteId) => availableSiteIds.has(siteId))
      if (availableSelectedSiteIds.length === 0) {
        setSelectedSiteIds(null)
        return
      }

      if (!areSameSiteIdSets(availableSelectedSiteIds, normalizedSelectedSiteIds)) {
        setSelectedSiteIds(availableSelectedSiteIds)
        return
      }
    }

    if (selectedSiteId !== null && !availableSiteIds.has(selectedSiteId)) {
      setSelectedSiteIds(null)
    }
  }, [hasResolvedSiteLoad, selectedSiteId, selectedSiteIds, setSelectedSiteIds, siteOptions])

  const [expandedNodeIds, setExpandedNodeIds] = useState<Record<string, boolean>>({})
  const { topLevelSites, rootNodes, ungroupedSites, sitePathById, siteIdsByNodeId, collapsedSiteIds } = useMemo(
    () => buildSiteTree(siteOptions),
    [siteOptions]
  )
  const nodeSortLabelsById = useMemo(() => buildNodeSortLabels(rootNodes), [rootNodes])
  const normalizedSelectedSiteIds = useMemo(() => {
    const normalized = normalizeSiteIds(selectedSiteIds)
    return normalized.length > 0 ? normalized : null
  }, [selectedSiteIds])
  const selectedSite = useMemo(
    () => {
      if (selectedSiteId !== null) {
        return siteOptions.find((site) => site.id === selectedSiteId) ?? null
      }

      if (normalizedSelectedSiteIds?.length === 1) {
        return siteOptions.find((site) => site.id === normalizedSelectedSiteIds[0]) ?? null
      }

      return null
    },
    [normalizedSelectedSiteIds, selectedSiteId, siteOptions]
  )
  const selectedGroupNode = useMemo(() => {
    if (selectedSite !== null || normalizedSelectedSiteIds === null) {
      return null
    }

    const findMatchingNode = (nodes: SiteTreeNode[], visitedNodeIds: Set<string> = new Set()): SiteTreeNode | null => {
      for (const node of nodes) {
        if (visitedNodeIds.has(node.id)) {
          continue
        }

        const nextVisitedNodeIds = new Set(visitedNodeIds)
        nextVisitedNodeIds.add(node.id)
        const nodeSiteIds = siteIdsByNodeId.get(node.id) ?? []
        if (areSameSiteIdSets(nodeSiteIds, normalizedSelectedSiteIds)) {
          return node
        }

        const childMatch = findMatchingNode(node.childOrganizationUnits, nextVisitedNodeIds)
        if (childMatch) {
          return childMatch
        }
      }

      return null
    }

    return findMatchingNode(rootNodes)
  }, [normalizedSelectedSiteIds, rootNodes, selectedSite, siteIdsByNodeId])
  const normalizedSiteSearchTerm = siteSearchTerm.trim().toLowerCase()
  const hasSiteSearch = normalizedSiteSearchTerm.length > 0
  const filteredTopLevelSites = useMemo(
    () =>
      hasSiteSearch
        ? topLevelSites.filter((site) => matchesSearchTerm(site.name, normalizedSiteSearchTerm))
        : topLevelSites,
    [hasSiteSearch, normalizedSiteSearchTerm, topLevelSites]
  )
  const filteredRootNodes = useMemo(
    () => (hasSiteSearch ? filterSiteTreeNodes(rootNodes, normalizedSiteSearchTerm) : rootNodes),
    [hasSiteSearch, normalizedSiteSearchTerm, rootNodes]
  )
  const filteredUngroupedSites = useMemo(
    () =>
      hasSiteSearch
        ? ungroupedSites.filter((site) => matchesSearchTerm(site.name, normalizedSiteSearchTerm))
        : ungroupedSites,
    [hasSiteSearch, normalizedSiteSearchTerm, ungroupedSites]
  )
  const rootEntries = useMemo(
    () =>
      [
        ...filteredTopLevelSites.map((site) => ({
          type: 'site' as const,
          key: `site-${site.id}`,
          sortLabel: getSiteSortLabel(site, collapsedSiteIds),
          site,
        })),
        ...filteredRootNodes.map((node) => ({
          type: 'group' as const,
          key: `group-${node.id}`,
          sortLabel: nodeSortLabelsById.get(node.id) ?? node.name,
          node,
        })),
        ...filteredUngroupedSites.map((site) => ({
          type: 'site' as const,
          key: `ungrouped-site-${site.id}`,
          sortLabel: getSiteSortLabel(site, collapsedSiteIds),
          site,
        })),
      ].sort((left, right) => compareSiteSelectorLabels(left.sortLabel, right.sortLabel)),
    [collapsedSiteIds, filteredRootNodes, filteredTopLevelSites, filteredUngroupedSites, nodeSortLabelsById]
  )

  const selectedSiteLabel =
    selectedSite?.name ??
    selectedGroupNode?.name ??
    (normalizedSelectedSiteIds !== null
      ? `Selected Group (${normalizedSelectedSiteIds.length} site${normalizedSelectedSiteIds.length === 1 ? '' : 's'})`
      : 'Charge Hub (All Sites)')

  useEffect(() => {
    if (selectedSiteId === null) {
      return
    }

    const selectedSitePath = sitePathById.get(selectedSiteId)
    if (!selectedSitePath || selectedSitePath.length === 0) {
      return
    }

    setExpandedNodeIds((previous) => {
      let hasChanges = false
      const next = { ...previous }

      selectedSitePath.forEach((nodeId) => {
        if (!next[nodeId]) {
          next[nodeId] = true
          hasChanges = true
        }
      })

      return hasChanges ? next : previous
    })
  }, [selectedSiteId, sitePathById])

  useEffect(() => {
    if (!selectedGroupNode) {
      return
    }

    const selectedGroupPath = findNodePathById(rootNodes, selectedGroupNode.id)
    if (!selectedGroupPath || selectedGroupPath.length === 0) {
      return
    }

    setExpandedNodeIds((previous) => {
      let hasChanges = false
      const next = { ...previous }

      selectedGroupPath.forEach((nodeId) => {
        if (!next[nodeId]) {
          next[nodeId] = true
          hasChanges = true
        }
      })

      return hasChanges ? next : previous
    })
  }, [rootNodes, selectedGroupNode])

  const toggleNode = (nodeId: string) => {
    setExpandedNodeIds((previous) => ({
      ...previous,
      [nodeId]: !previous[nodeId],
    }))
  }

  const updateSiteSelectorDropdownPosition = useCallback(() => {
    const triggerElement = siteSelectorButtonRef.current
    if (!triggerElement) {
      return
    }

    const triggerRect = triggerElement.getBoundingClientRect()
    const viewportPadding = 16
    const maxWidth = Math.max(320, Math.min(760, window.innerWidth - viewportPadding * 2))
    const width = Math.min(Math.max(triggerRect.width * 2, 420), maxWidth)
    const left = Math.min(
      Math.max(viewportPadding, triggerRect.left),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
    )
    const top = triggerRect.bottom + 8
    const maxHeight = Math.max(280, window.innerHeight - top - viewportPadding)

    setSiteSelectorDropdownStyle({
      position: 'fixed',
      top,
      left,
      width,
      maxWidth,
      zIndex: 2000,
      maxHeight,
      overflowY: 'auto',
    })
  }, [])

  useEffect(() => {
    if (!isSiteSelectorOpen) {
      setSiteSearchTerm('')
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const targetNode = event.target as Node
      if (siteSelectorRef.current?.contains(targetNode)) {
        return
      }

      if (siteSelectorDropdownRef.current?.contains(targetNode)) {
        return
      }

      setIsSiteSelectorOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isSiteSelectorOpen])

  useEffect(() => {
    if (!isSiteSelectorOpen) {
      hasAutoScrolledOnOpenRef.current = false
      return
    }

    if (isSiteLoading || hasAutoScrolledOnOpenRef.current) {
      return
    }

    hasAutoScrolledOnOpenRef.current = true
    let firstFrameId = 0
    let secondFrameId = 0

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        const selectedOption = siteSelectorDropdownRef.current?.querySelector<HTMLElement>(
          '[data-site-option-selected="true"]'
        )
        selectedOption?.scrollIntoView({block: 'nearest'})
      })
    })

    return () => {
      if (firstFrameId) {
        window.cancelAnimationFrame(firstFrameId)
      }
      if (secondFrameId) {
        window.cancelAnimationFrame(secondFrameId)
      }
    }
  }, [isSiteLoading, isSiteSelectorOpen])

  useEffect(() => {
    if (!isSiteSelectorOpen) {
      return
    }

    updateSiteSelectorDropdownPosition()

    const handleViewportChange = () => {
      updateSiteSelectorDropdownPosition()
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [isSiteSelectorOpen, updateSiteSelectorDropdownPosition])

  return (
    <>
      <div className='menu-item px-6'>
        <div className='menu-content px-0'>
          <div ref={siteSelectorRef} className='position-relative'>
            <button
              ref={siteSelectorButtonRef}
              type='button'
              className='btn btn-sm btn-light w-100 d-flex align-items-center justify-content-between border border-gray-300'
              onClick={() => setIsSiteSelectorOpen((previous) => !previous)}
              title={selectedSiteLabel}
              aria-label={selectedSiteLabel}
            >
              <span className='text-truncate pe-2' title={selectedSiteLabel}>
                {selectedSiteLabel}
              </span>
              <i className={`bi ${isSiteSelectorOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-7`}></i>
            </button>
          </div>
        </div>
      </div>

      {isSiteSelectorOpen ? (
        <Portal>
          <div
            ref={siteSelectorDropdownRef}
            className='bg-white border border-gray-300 rounded shadow-sm p-3'
            style={siteSelectorDropdownStyle}
          >
            <div
              style={{
                position: 'sticky',
                top: -12,
                zIndex: 1,
                background: '#fff',
                paddingTop: 0,
                paddingBottom: 8,
                marginBottom: 8,
              }}
            >
              <input
                type='search'
                className='form-control form-control-sm'
                placeholder='Search groups or sites'
                value={siteSearchTerm}
                onChange={(event) => setSiteSearchTerm(event.target.value)}
                disabled={isSiteLoading}
              />
            </div>

            {isSiteLoading ? <div className='text-muted fs-8'>Loading sites...</div> : null}

            <button
              type='button'
              className={`${getSiteOptionClassName(selectedSiteId === null && normalizedSelectedSiteIds === null)} mb-2`}
              style={getSiteOptionStyle(selectedSiteId === null && normalizedSelectedSiteIds === null)}
              data-site-option-selected={
                selectedSiteId === null && normalizedSelectedSiteIds === null ? 'true' : undefined
              }
              onClick={() => {
                setSelectedSiteIds(null)
                setIsSiteSelectorOpen(false)
              }}
              disabled={isSiteLoading}
            >
              Charge Hub (All Sites)
            </button>

            {!isSiteLoading ? (
              <>
                {rootEntries.map((entry) =>
                  entry.type === 'site' ? (
                    (() => {
                      const isSelected = isSiteSelected(selectedSiteId, normalizedSelectedSiteIds, entry.site.id)
                      return (
                        <button
                          key={entry.key}
                          type='button'
                          className={`${getSiteOptionClassName(isSelected)} mb-1`}
                          style={getSiteOptionStyle(isSelected)}
                          data-site-option-selected={isSelected ? 'true' : undefined}
                          onClick={() => {
                            setSelectedSiteId(entry.site.id)
                            setIsSiteSelectorOpen(false)
                          }}
                          disabled={isSiteLoading}
                          title={entry.site.name}
                        >
                          <i className={getSiteIconClassName(isSelected)}></i>
                          <span>{entry.site.name}</span>
                        </button>
                      )
                    })()
                  ) : (
                    <SiteTreeItem
                      key={entry.key}
                      node={entry.node}
                      expandedNodeIds={expandedNodeIds}
                      toggleNode={toggleNode}
                      selectedSiteId={selectedSiteId}
                      selectedSiteIds={normalizedSelectedSiteIds}
                      setSelectedSiteId={setSelectedSiteId}
                      setSelectedSiteIds={setSelectedSiteIds}
                      siteIdsByNodeId={siteIdsByNodeId}
                      nodeSortLabelsById={nodeSortLabelsById}
                      collapsedSiteIds={collapsedSiteIds}
                      isSiteLoading={isSiteLoading}
                      forceExpanded={hasSiteSearch}
                      onSiteSelected={() => setIsSiteSelectorOpen(false)}
                    />
                  )
                )}
              </>
            ) : null}
          </div>
        </Portal>
      ) : null}

      <AsideMenuItem
          to="/dashboard"
          title={intl.formatMessage({ id: 'MENU.DASHBOARD' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/home.svg"
          pagePermission={PAGE_PERMISSION.Dashboard}
        />
      <AsideMenuItem
          to="/chargers"
          title={intl.formatMessage({ id: 'MENU.CHARGERS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/plug.svg"
          pagePermission={PAGE_PERMISSION.Chargers}
        />
      <AsideMenuItem
          to="/faulted-assets"
          title={intl.formatMessage({ id: 'MENU.FAULTED_ASSETS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/warning.svg"
          pagePermission={PAGE_PERMISSION.Faulted_Assets}
        />
      {/* <AsideMenuItem
          to="/service-tickets"
          title={intl.formatMessage({ id: 'MENU.SERVICE_TICKETS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/headset.svg"
          pagePermission={PAGE_PERMISSION.Service_Tickets}
        /> */}
      <AsideMenuItem
          to="/foyer-view"
          title={intl.formatMessage({ id: 'MENU.FOYER_VIEW' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/monitor.svg"
          pagePermission={PAGE_PERMISSION.FoyerView}
        />
      <AsideMenuItem
          to="/reports"
          title={intl.formatMessage({ id: 'MENU.REPORTS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/chart-line.svg"
          pagePermission={PAGE_PERMISSION.Reports}
        />

      <div className="menu-item">
        <div className="menu-content">
          <div className="separator mx-1 my-4"></div>
        </div>
      </div>

      <AsideMenuItemWithSub
        to="/"
        title="Administration"
        fontIcon="bi-chat-left"
        icon="/media/icons/tims/menu-administration.svg"
        pagePermissions={[
          PAGE_PERMISSION.Admin_Users,
          PAGE_PERMISSION.Admin_Groups,
          PAGE_PERMISSION.Admin_Roles,
          PAGE_PERMISSION.Admin_Settings,
          PAGE_PERMISSION.Admin_Charts,
          PAGE_PERMISSION.Admin_Audit
        ]}>
        <AsideMenuItem
          to="/user"
          title={intl.formatMessage({ id: 'MENU.USERS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-users.svg"
          pagePermission={PAGE_PERMISSION.Admin_Users}
        />

        <AsideMenuItem
          to="/groups"
          title={intl.formatMessage({ id: 'MENU.GROUPS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-groups.svg"
          pagePermission={PAGE_PERMISSION.Admin_Groups}
        />

        <AsideMenuItem
          to="/role"
          title={intl.formatMessage({ id: 'MENU.ROLES' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-roles.svg"
          pagePermission={PAGE_PERMISSION.Admin_Roles}
        />

        {/* <AsideMenuItem
          to="/settings"
          title={intl.formatMessage({ id: 'MENU.SETTINGS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/menu-settings.svg"
          pagePermission={PAGE_PERMISSION.Admin_Settings}
        />

        <AsideMenuItem
          to="/charts"
          title={intl.formatMessage({ id: 'MENU.CHARTS' })}
          fontIcon="bi-chat-left"
          icon="/media/icons/tims/list-check.svg"
          pagePermission={PAGE_PERMISSION.Admin_Charts}
        />

        <AsideMenuItem
          to='/audit'
          title={intl.formatMessage({ id: 'MENU.AUDIT' })}
          fontIcon='bi-chat-left'
          icon='/media/icons/tims/menu-audit.svg'
          pagePermission={ PAGE_PERMISSION.Admin_Audit }
        /> */}
      </AsideMenuItemWithSub>
    </>
  )
}
